import * as React from 'react';
import { Box, Paper, Divider } from '@mui/material';

export interface PageLayoutProps {
  /**
   * Header della pagina (titolo, breadcrumb, toolbar, etc.)
   */
  header?: React.ReactNode;

  /**
   * Contenuto principale della pagina (scrollabile)
   */
  children: React.ReactNode;

  /**
   * Barra delle azioni in basso (tab, pulsanti, etc.)
   */
  actions?: React.ReactNode;

  /**
   * Mostra o nasconde il divider sotto l'header
   * @default true
   */
  showDivider?: boolean;

  /**
   * Props addizionali per il Paper principale
   */
  paperProps?: React.ComponentProps<typeof Paper>;

  /**
   * Props addizionali per il Paper delle actions
   */
  actionsPaperProps?: React.ComponentProps<typeof Paper>;
}

/**
 * Layout generico per le pagine con header, contenuto scrollabile e barra azioni
 *
 * @example
 * ```tsx
 * <PageLayout
 *   header={<Typography variant="h5">Customer Details</Typography>}
 *   actions={
 *     <Button onClick={handleSave}>Salva</Button>
 *   }
 * >
 *   <div>Contenuto della pagina</div>
 * </PageLayout>
 * ```
 */
export const PageLayout: React.FC<PageLayoutProps> = ({
  header,
  children,
  actions,
  showDivider = true,
  paperProps,
  actionsPaperProps,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
    >
      {/* Main content area */}
      <Paper
        sx={{
          p: 1,
          borderRadius: 2,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...paperProps?.sx,
        }}
        {...paperProps}
      >
        {/* Header */}
        {header && (
          <>
            <Box
              sx={{
                mb: showDivider ? 1 : 0,
                display: 'flex',
                verticalAlign: 'baseline',
                height: '3rem',
                minHeight: '3rem',
                justifyItems: 'baseline',
                alignItems: 'center',
                px: 2,
              }}
            >
              {header}
            </Box>
            {showDivider && <Divider />}
          </>
        )}

        {/* Scrollable content area */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            // Quando è presente la barra azioni flottante (position: absolute, bottom: 16),
            // riserva uno spazio in fondo al contenuto scrollabile pari alla sua altezza
            // approssimativa + margini, così l'ultima riga di un form/tabella non resta
            // nascosta sotto la barra quando si scrolla fino in fondo.
            pb: actions ? '88px' : 0,
          }}
        >
          {children}
        </Box>
      </Paper>

      {/* Floating actions bar at bottom */}
      {actions && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 16,
            pr: 2,
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          <Paper
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 1,
              borderRadius: 2,
              width: 'auto',
              gap: 1,
              pointerEvents: 'auto',
              ...actionsPaperProps?.sx,
              backgroundColor: '#272727',
              boxShadow: '0 3px 5px rgba(0,0,0,0.4)',
            }}
            {...actionsPaperProps}
          >
            {actions}
          </Paper>
        </Box>
      )}
    </Box>
  );
};
